import { ApiError, createApiClient, type ApiClient, type ApiEnvelope } from '@fcms/ui'

/**
 * The one HTTP client for `/backoffice/v1`.
 *
 * Wiring, not policy: the token provider and the 401 handler are *installed*
 * by the auth store at start-up rather than imported here. If this module
 * imported the store and the store imported this module, the cycle would
 * resolve differently under Vite and under Vitest, which is the kind of bug
 * that only shows up in one of them.
 */

type TokenReader = () => string | null
type UnauthorizedHandler = () => boolean | Promise<boolean>

let readToken: TokenReader = () => null
let onUnauthorized: UnauthorizedHandler = () => false

/**
 * Installs the credential hooks. Called once, from the auth store's own
 * initialisation, before the first request leaves.
 */
export function installCredentials(hooks: {
  getToken: TokenReader
  onUnauthorized: UnauthorizedHandler
}): void {
  readToken = hooks.getToken
  onUnauthorized = hooks.onUnauthorized
}

export const api: ApiClient = createApiClient({
  // Same-origin. Caddy fronts the API and this bundle from one host in
  // production, and `vite.config.ts` proxies the same prefix in development,
  // so there is no environment-dependent base URL to get wrong.
  baseUrl: '/backoffice/v1',
  getToken: () => readToken(),
  onUnauthorized: () => onUnauthorized(),
  // 15s is generous for a jetty on a 3G link and short enough that a dead
  // socket surfaces as an error rather than a spinner nobody understands.
  timeoutMs: 15_000,
})

/**
 * POSTs a body VERBATIM, with no JSON encoding.
 *
 * ============================================================================
 * The design system's client always `JSON.stringify`s the body — correct for
 * every operation on this surface but one. `POST /payouts/runs/{id}/confirm`
 * documents its body as `text/csv` and says why: "the request body is the RAW
 * file the bank returned, not JSON — any repackaging step is a chance to
 * mangle it." Sending it through the wrapper would deliver a JSON-quoted
 * string with every newline escaped, and the parser would reject a file that
 * was perfectly good.
 *
 * So this one path uses `fetch` directly. It reuses the wrapper's own
 * conventions — same base URL, same bearer, same request id header, same
 * envelope unwrap, same typed error — so nothing about the call is special
 * except that the bytes arrive unaltered.
 * ============================================================================
 */
export async function postRaw<T>(
  path: string,
  body: string,
  options: { contentType: string; idempotencyKey?: string; timeoutMs?: number },
): Promise<T> {
  const url = api.resolve(path)
  const requestId = idempotencyKey('req').slice(4)
  const controller = new AbortController()
  const timeout = options.timeoutMs ?? 60_000
  const timer = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : undefined

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': options.contentType,
    'X-Request-Id': requestId,
  }
  const token = readToken()
  if (token) headers.Authorization = `Bearer ${token}`
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey

  let response: Response
  try {
    response = await fetch(url, { method: 'POST', headers, body, signal: controller.signal })
  } catch (cause) {
    throw new ApiError({
      code: controller.signal.aborted ? 'timeout' : 'network_error',
      message: 'The upload did not reach the server.',
      status: 0,
      cause,
    })
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }

  let envelope: ApiEnvelope<T>
  try {
    envelope = (await response.json()) as ApiEnvelope<T>
  } catch (cause) {
    throw new ApiError({
      code: 'malformed_response',
      message: `The server returned a response that is not JSON (HTTP ${response.status}).`,
      status: response.status,
      requestId,
      cause,
    })
  }

  if (!response.ok || envelope.success === false) {
    throw new ApiError({
      code: envelope.error?.code ?? 'internal_error',
      message: envelope.error?.message ?? `Request failed with HTTP ${response.status}.`,
      status: response.status,
      fields: envelope.error?.fields,
      details: envelope.error?.details,
      requestId: envelope.meta?.request_id ?? requestId,
    })
  }

  return envelope.data as T
}

/**
 * A stable idempotency key for one logical operation.
 *
 * Every partner write and every device-ingested write requires one, and the
 * back office's finalize/void/approve actions are exactly the operations where
 * a double-submit is expensive. The key must be generated ONCE per intent and
 * reused across retries — generating it inside the retry would defeat it — so
 * call sites mint it when the user confirms, not when the request is sent.
 */
export function idempotencyKey(scope: string): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  return `${scope}:${random}`
}
